/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { type MessageDescriptor, FormattedMessage } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

type FooterLinkItem = {
	label: MessageDescriptor;
	/**
	 * Destination URL for the link. Set to the literal string `'TODO'`
	 * for items whose final destination has not yet been decided; these
	 * placeholders will be replaced before the footer is wired up to
	 * real anchor `href`s.
	 */
	url: string;
};

const ITEMS: FooterLinkItem[] = [
	{ label: messages.partners, url: 'https://www.atlassian.com/partners' },
	{ label: messages.trainingCertification, url: 'https://www.atlassian.com/university' },
	{
		label: messages.documentation,
		url: 'https://confluence.atlassian.com/display/ALLDOC/Atlassian+Documentation',
	},
	{ label: messages.developerResources, url: 'https://www.atlassian.com/developers' },
	{ label: messages.enterpriseServices, url: 'https://www.atlassian.com/enterprise/services' },
];

// Destination URL for the "See all resources →" CTA, taken from
// atlassian.com's footer.
const SEE_ALL_RESOURCES_URL = 'https://www.atlassian.com/resources';

const styles = cssMap({
	column: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignSelf: 'stretch',
		flex: '1 0 0',
		gap: token('space.300'),
	},
	columnInner: {
		display: 'flex',
		flexDirection: 'column',
		alignSelf: 'stretch',
		gap: token('space.300'),
	},
	heading: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.semibold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '12px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// Figma spec uses `Neutral/Light/400` (#B7B9BE) for muted text
		// on the dark `#18191A` footer surface. There is no semantic
		// ADS token for muted text on a custom dark surface, so the
		// hex is inlined to match the design exactly.
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		margin: 0,
	},
	items: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		alignSelf: 'stretch',
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	itemLink: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '12px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		textDecoration: 'none',
		textAlign: 'left',
	},
	cta: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '12px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		textDecoration: 'none',
		margin: 0,
		alignSelf: 'flex-start',
	},
});

/**
 * "Learn" column of the microsite footer.
 *
 * Includes a "See all resources →" CTA pinned to the bottom of the
 * column per the Figma design.
 *
 * Currently presentational - real anchor `href`s will be added in a
 * follow-up change once destination URLs are decided.
 */
export const LearnColumn = (): JSX.Element => (
	<Box xcss={styles.column}>
		<Box xcss={styles.columnInner}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.heading}>
				<FormattedMessage {...messages.heading} />
			</p>
			<ul css={styles.items}>
				{ITEMS.map((item) => (
					<li key={item.label.id}>
						{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
						<a css={styles.itemLink} href={item.url}>
							<FormattedMessage {...item.label} />
						</a>
					</li>
				))}
			</ul>
		</Box>
		{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
		<a css={styles.cta} href={SEE_ALL_RESOURCES_URL}>
			<FormattedMessage {...messages.seeAllResources} />
		</a>
	</Box>
);
